import React, { useMemo } from 'react';
import { AutoSizer, CellMeasurer, CellMeasurerCache, List } from 'react-virtualized';
import styles from './KeypadContacts.module.scss';
import { ContactDisplayItem, ContactItemType, useContacts, useGetfirstOrLastName } from '../../sidebars/Contact/ContactListHooks';
import { useTypedDispatch, useTypedSelector } from '../../../redux/hooks';
import { sanitizeStringForRegex, useDebounce } from '../../../helpers';
import Icon from '../../Icon';
import {
    IHuntGroup,
    IPhonebookContact,
    isHuntGroup,
    isUser,
    IUser
} from '../../../types';
import {
    ExtensionCallRoute,
    removeUserForGroupCall,
    selectPhoneSettingByKey,
    selectRoutesWithExtension,
    selectUsersForGroupCall
} from '../../../redux/slices';
import { KeypadContactRow } from './children/KeypadContactRow';
import { usePolyglot } from '../../../context/Polyglot';
import { ContactIcon } from '../../sidebars/Contact/children';
import ContactProvider from '../../../context/ContactContext/context';
import { useKeypadContactsKeydown } from './hooks';
import AriaListFocusContainer from '../../AriaComponents/AriaListFocusContainer';
import AriaButton from '../../AriaComponents/AriaButton';
import KeypadRouteRow from './children/KeypadRouteRow';

interface keypadContactsProps {
    searchTerm: string;
    transfer?: boolean;
    addCall?: boolean;
    notInCall?: boolean;
    addToCall?: boolean;
    isSmall?: boolean;
}

interface InvitationContactProps {
    contact: IUser | IPhonebookContact | IHuntGroup;
    notInCall?: boolean;
}

export const InvitationContact: React.FC<InvitationContactProps> = ({ contact, notInCall }) => {
    const dispatch = useTypedDispatch()

    const display =
        // eslint-disable-next-line no-nested-ternary
        isUser(contact)
            ? contact.nickname
            // eslint-disable-next-line no-nested-ternary
            : isHuntGroup(contact)
                ? contact.name
                : contact.first_name || contact.last_name
                    ? `${contact.first_name || ''} ${contact.last_name || ''}`
                    : contact.company_name

    return (
        <li
            className={styles.invitation_contact}
            data-in-call={!notInCall || null}
        >
            <p>{display || ("selected_number" in contact ? contact.selected_number : 'error')}</p>
            <AriaButton
                onClick={() => {
                    dispatch(
                        removeUserForGroupCall(contact.uuid)
                    )
                }}
            >
                <Icon name='cross' height={20} width={20} />
            </AriaButton>
        </li>
    )
}

type contextTypes =
    'new'
    | 'group'
    | 'transfer';

type KeypadContactItem = ContactDisplayItem | { type: 5, data: ExtensionCallRoute };

export const KeypadContacts: React.FC<keypadContactsProps> = ({
    searchTerm,
    transfer,
    addCall,
    notInCall,
    addToCall,
    isSmall,
}) => {
    let context: contextTypes;

    switch (true) {
        case addCall:
            context = 'new'
            break;
        case addToCall:
            context = 'group'
            break;
        case transfer:
            context = 'transfer'
            break;
        default:
            context = 'new'
    }

    const invitationList = useTypedSelector(selectUsersForGroupCall)
    const phonebooksInContacts = useTypedSelector(state => selectPhoneSettingByKey(state, 'phonebooksInContacts'));

    const searchRegex = searchTerm ? new RegExp(sanitizeStringForRegex(searchTerm), 'i') : null;

    const routes = useTypedSelector(state => {
        const base = selectRoutesWithExtension(state);

        if (!searchRegex) {
            return base;
        }

        return base.filter(route => searchRegex.test(route.name));
    });

    const getFirstOrLastName = useGetfirstOrLastName();

    const { t } = usePolyglot();

    const searchTermDebounce = useDebounce(searchTerm, 440);

    const handleKeydown = useKeypadContactsKeydown()

    const { favourites, mainDisplay, outerDisplay } = useContacts({
        searchQuery: searchTermDebounce,
        filter: (!phonebooksInContacts && transfer) ? ['Internal Users', 'Hunt Groups'] : [],
        exclusions: ['Tenants']
    });

    const fullList = useMemo(() => {
        if (context !== 'transfer' || !routes) {
            return mainDisplay
        }

        const getSortName = (item: ContactDisplayItem): string => {
            switch (item.type) {
                case ContactItemType.User:
                    return getFirstOrLastName({ user: item.data });
                case ContactItemType.HuntGroup:
                    return item.data.name;
                case ContactItemType.PhonebookContact:
                    return getFirstOrLastName({ contact: item.data });
                case ContactItemType.BridgeClient:
                    return item.data.name;
                default:
                    return '';
            }
        };

        const list: KeypadContactItem[] = Array(routes.length + mainDisplay.length);

        let routeIdx = 0;
        let mainIdx = 0;

        while (routeIdx < routes.length || mainIdx < mainDisplay.length) {
            const route = routes[routeIdx];
            const main = mainDisplay[mainIdx];

            if (!route && !main) {
                break;
            }
            if (!route) {
                list[routeIdx + mainIdx] = main;
                mainIdx += 1;
            } else if (!main || getSortName(main).localeCompare(route.name) > 0) {
                list[routeIdx + mainIdx] = { type: 5, data: route };
                routeIdx += 1;
            } else {
                list[routeIdx + mainIdx] = main;
                mainIdx += 1;
            }

        };

        return list;
    }, [routes.length, mainDisplay.length, getFirstOrLastName])

    const displayContacts: KeypadContactItem[] = useMemo(() => {
        const contacts = (favourites as KeypadContactItem[]).concat(fullList);

        if (invitationList.length > 0 && addToCall) {
            return contacts.filter(contact =>
                !invitationList.some(invite => invite.uuid === contact.data.uuid)
            );
        }

        return contacts;
    }, [favourites.length, mainDisplay.length, outerDisplay.length, invitationList.length, searchTermDebounce?.length]);

    const cache = new CellMeasurerCache({
        fixedWidth: true,
        defaultHeight: 40,
    });

    const rowRenderer = ({
        key,
        index,
        style,
        parent
    }) => {
        const contact = displayContacts?.[index];

        if (!contact || contact.type === 4) {
            return null;
        }

        return (
            <CellMeasurer
                key={key}
                cache={cache}
                parent={parent}
                columnIndex={0}
                rowIndex={index}
            >
                <div
                    style={style}
                    data-reverse-menu={(index > 6 && (index - (displayContacts.length - 4) >= 0)) || null}
                    onKeyDown={handleKeydown}
                >
                    {contact.type === 5 ? (
                        <KeypadRouteRow
                            key={contact.data.uuid}
                            route={contact.data}
                            context={context}
                        />
                    ) : (
                        <ContactProvider value={{ contact: contact.data }}>
                            <KeypadContactRow
                                key={contact.data.uuid}
                                context={context}
                                icon={<ContactIcon size={28} />}
                            />
                        </ContactProvider>
                    )}
                </div>
            </CellMeasurer>
        )
    }

    const scaleSize = () => {
        switch (true) {
            case notInCall && addToCall:
                return isSmall ? 2 : 1;
            case addToCall:
                return isSmall ? 6 : 5;
            case notInCall:
                return 3
            default:
                return 4
        }
    }

    return (
        <AriaListFocusContainer
            className={styles.container}
            title='Contacts'
            dataTags={{
                'data-scale': String(scaleSize()),
                'data-in-call': !notInCall || null,
            }}
        >
            {(displayContacts?.length < 1) ? (
                <div className='list-placeholder'>
                    <p>{searchTerm ? t("phrases.no_results_match_your_search") : t("phrases.no_contacts")}</p>
                </div>
            ) : null}
            <AutoSizer>
                {({ height }) => (
                    <List
                        width={246}
                        height={height}
                        rowCount={displayContacts?.length}
                        rowRenderer={rowRenderer}
                        overscanRowCount={3}
                        rowHeight={cache.rowHeight}
                        deferredMeasurementCache={cache}
                        className={styles.windowed_contact_list_container}
                        tabIndex={-1}
                    />
                )}
            </AutoSizer>
        </AriaListFocusContainer>
    );
}

export default KeypadContacts
